import _ from 'lodash';
import React from 'react';
import { UNIFORM_PROPS } from 'common/constants';
import { emailRegExp, telRegExp } from 'common/utils/regexp';

import { FIELD_BB_ERROR_TYPE } from 'client/components/common/BuildingBlocks/Fields/constants';
import { StoryCard } from '../StoryCard';
import { FormContext } from './FormContext';
import { State, ValidateFnType, ValidationResultType, RegisterFieldFnType, initialState } from './types';

// true === valid
const validations = {
	[FIELD_BB_ERROR_TYPE.REQUIRED]: (v: string | boolean) => (typeof v === 'boolean' ? v : _.trim(v) !== ''),
	[FIELD_BB_ERROR_TYPE.EMAIL]: v => emailRegExp.test(v),
	[FIELD_BB_ERROR_TYPE.TEL]: v => telRegExp.test(v),
};

class FormCard extends StoryCard<any> {
	state: State = { ...initialState };

	fields = {};

	get validationResult(): ValidationResultType {
		return _.pick(this.state, ['isValid', 'errors']);
	}

	get providerValues() {
		return {
			validate: this.validate,
			registerField: this.registerField,
			...this.validationResult,
		};
	}

	registerField: RegisterFieldFnType = field => {
		this.fields[field._id] = field;
	};

	validate: ValidateFnType = async formData => {
		const errors = {}; // e.g.: { id1: ['required'], id2: ['email', 'required'] }

		/**
		 * @param errorType {!string}
		 * @param fieldType {!string}
		 * @param fieldRequired {!boolean}
		 * @returns {boolean}
		 */
		function shouldValidate({ errorType, componentProps: { fieldType, fieldRequired } }) {
			switch (errorType) {
				case FIELD_BB_ERROR_TYPE.REQUIRED:
					return fieldRequired;
				default:
					return errorType === fieldType;
			}
		}

		// Validate
		Object.entries(formData).forEach(([key, value]) => {
			const field = this.fields[key];

			if (!field) {
				/*
				 NOTE:
				 Expected that "global" fields doesn't have Form card context to register itself.
				 Other fields must be listed in "this.fields"

				 "global" fields won't be handled by this component.
				 It's expected behaviour, because there is no sense in "global" fields.
				 If sometime it would be needed to validate "global" fields,
				 think about how we can provide context of this component to higher components like <StoryElements />
				 */

				return;
			}

			const { componentProps } = field.uiConfig;

			_.forOwn(componentProps[UNIFORM_PROPS.fieldError], (v, k) => {
				const result = shouldValidate({ errorType: k, componentProps })
					? validations[k](value.type === 'checkbox' ? value.attributes?.checked ?? false : value.data)
					: true;

				if (!result) {
					if (!errors[key]) {
						_.set(errors, key, []);
					}
					errors[key].push(k);
				}
			});
		});

		const hasErrors = !_.isEmpty(errors);

		await this.setState(hasErrors ? { isValid: false, errors } : initialState);

		return this.validationResult;
	};

	onSubmit = event => {
		event.preventDefault();
	};

	render() {
		return (
			<div className="form-card">
				<form autoComplete="off" onSubmit={this.onSubmit}>
					<FormContext.Provider value={this.providerValues}>{super.renderCard()}</FormContext.Provider>
				</form>
			</div>
		);
	}
}

export default FormCard;
